import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ProductFitQuizProps {
  isOpen?: boolean;
  onClose?: () => void;
  mode?: 'modal' | 'embedded';
}

interface QuizData {
  // Experience & Background
  cadExperience: string;
  confidence3D: string;
  role: string;
  
  // Projects & Work Type
  projectTypes: string;
  scaffoldComplexity: string;
  materials: string;
  engineeringImportance: string;
  
  // Business & Resourcing
  designCapacity: string;
  teamTechnical: string;
  engineeringSignoff: string;
  
  // What Matters Most
  priority: string;
  speedVsPower: string;
  speedVsAccuracy: string;
  softwarePreference: string;
  siteChanges: string;
  visualsVsPerformance: string;
  
  // Practical Use & Business Goals
  tenderDesigns: string;
  pricingAccuracy: string;
  quantitiesData: string;
  
  // Future Direction & Wrap-Up
  futureProjects: string;
  startSimple: string;
  motivation: string;
  frustration: string;
}

interface QuizResult {
  sketchupScore: number;
  teklaScore: number;
  recommendation: string;
  explanation: string;
  benefits: string[];
  nextSteps: string[];
}

// Custom Dropdown Component
interface DropdownOption {
  value: string | number;
  label: string;
}

interface CustomDropdownProps {
  value: string | number;
  onChange: (value: string | number) => void;
  options: DropdownOption[];
  placeholder: string;
  hasError?: boolean;
  isNumeric?: boolean;
  emptyValue?: string | number;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder,
  hasError = false,
  isNumeric = false,
  emptyValue
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newValue = isNumeric ? Number(e.target.value) : e.target.value;
    onChange(newValue);
  };

  const defaultEmptyValue = emptyValue !== undefined ? emptyValue : (isNumeric ? 0 : "");
  const isEmptyValue = value === defaultEmptyValue;

  return (
    <div className="relative">
      <select
        value={value}
        onChange={handleChange}
        className={`w-full pl-4 pr-12 py-3 border rounded-full focus:ring-2 focus:ring-[#0479d2] focus:border-[#0479d2] outline-none transition-colors bg-white appearance-none ${
          hasError ? 'border-red-500' : 'border-[#0479d2]'
        }`}
        style={{
          color: isEmptyValue ? 'rgb(156, 163, 175)' : 'rgb(17, 24, 39)' // text-gray-400 vs text-gray-900
        }}
      >
      <option value={defaultEmptyValue} style={{ color: 'rgb(156, 163, 175)' }}>
        {placeholder}
      </option>
      {options.map((option) => (
        <option key={option.value} value={option.value} style={{ color: 'rgb(17, 24, 39)' }}>
          {option.label}
        </option>
      ))}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
};

export default function ProductFitQuiz({ isOpen = true, onClose, mode = 'modal' }: ProductFitQuizProps) {
  const [step, setStep] = useState(1);
  const [showResults, setShowResults] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Reusable style classes
  const labelClass = "block text-sm font-medium text-gray-900 mb-2";
  
  // Ordered fields per step for validation focus/scroll
  const stepFieldOrder: Record<number, Array<keyof QuizData>> = {
    1: ['cadExperience', 'confidence3D', 'role'],
    2: ['projectTypes', 'scaffoldComplexity', 'materials', 'engineeringImportance'],
    3: ['designCapacity', 'teamTechnical', 'engineeringSignoff'],
    4: ['priority', 'speedVsPower', 'speedVsAccuracy', 'softwarePreference', 'siteChanges', 'visualsVsPerformance'],
    5: ['tenderDesigns', 'pricingAccuracy', 'quantitiesData'],
    6: ['futureProjects', 'startSimple', 'motivation', 'frustration']
  };

  const scrollToFirstErrorForStep = (currentStep: number) => {
    const fields = stepFieldOrder[currentStep] || [];
    const firstErrorField = fields.find((fieldName) => !!errors[fieldName]);
    if (firstErrorField) {
      const el = document.getElementById(`field-${firstErrorField}`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  const [quizData, setQuizData] = useState<QuizData>({
    cadExperience: '',
    confidence3D: '',
    role: '',
    projectTypes: '',
    scaffoldComplexity: '',
    materials: '',
    engineeringImportance: '',
    designCapacity: '',
    teamTechnical: '',
    engineeringSignoff: '',
    priority: '',
    speedVsPower: '',
    speedVsAccuracy: '',
    softwarePreference: '',
    siteChanges: '',
    visualsVsPerformance: '',
    tenderDesigns: '',
    pricingAccuracy: '',
    quantitiesData: '',
    futureProjects: '',
    startSimple: '',
    motivation: '',
    frustration: ''
  });

  const calculateRecommendations = (): QuizResult => {
    let sketchupScore = 0;
    let teklaScore = 0;
    
    // Experience & Background Scoring
    if (quizData.cadExperience === 'none') sketchupScore += 1;
    else if (quizData.cadExperience === 'basic') sketchupScore += 1;
    else if (quizData.cadExperience === 'advanced') teklaScore += 1;
    
    if (quizData.confidence3D === 'not-confident') sketchupScore += 1;
    else if (quizData.confidence3D === 'fairly-confident') { sketchupScore += 0.5; teklaScore += 0.5; }
    else if (quizData.confidence3D === 'very-confident') teklaScore += 1;
    
    if (quizData.role === 'designer-engineer') teklaScore += 1;
    else if (quizData.role === 'owner-manager-scaffolder') sketchupScore += 1;
    
    // Projects & Work Type Scoring
    if (quizData.projectTypes === 'small-residential') sketchupScore += 1;
    else if (quizData.projectTypes === 'large-commercial') teklaScore += 1;
    else if (quizData.projectTypes === 'industrial-infrastructure') teklaScore += 1;
    
    if (quizData.scaffoldComplexity === 'simple-facades') sketchupScore += 1;
    else if (quizData.scaffoldComplexity === 'mix-both') { sketchupScore += 0.5; teklaScore += 0.5; }
    else if (quizData.scaffoldComplexity === 'complex-multilevel') teklaScore += 1;
    
    if (quizData.materials === 'modular') { sketchupScore += 0.5; teklaScore += 0.5; }
    else if (quizData.materials === 'tube-fitting') { sketchupScore += 0.4; teklaScore += 0.6; };
    
    if (quizData.engineeringImportance === 'not-important') sketchupScore += 1;
    else if (quizData.engineeringImportance === 'sometimes-helpful') { sketchupScore += 0.5; teklaScore += 0.5; }
    else if (quizData.engineeringImportance === 'critical') teklaScore += 1;
    
    // Business & Resourcing Scoring
    if (quizData.designCapacity === 'dedicated-team') teklaScore += 1;
    else if (quizData.designCapacity === 'one-designer') { sketchupScore += 0.5; teklaScore += 0.5; }
    else if (quizData.designCapacity === 'no-designer') sketchupScore += 1;
    
    if (quizData.teamTechnical === 'not-very') sketchupScore += 1;
    else if (quizData.teamTechnical === 'mixed') { sketchupScore += 0.5; teklaScore += 0.5; }
    else if (quizData.teamTechnical === 'highly-technical') teklaScore += 1;
    
    if (quizData.engineeringSignoff === 'external') sketchupScore += 1;
    else if (quizData.engineeringSignoff === 'internal') teklaScore += 1;
    else if (quizData.engineeringSignoff === 'not-required') sketchupScore += 1;
    
    // What Matters Most Scoring
    if (quizData.priority === 'costs-down') sketchupScore += 1;
    else if (quizData.priority === 'performance-capability') teklaScore += 1;
    
    if (quizData.speedVsPower === 'get-running-fast') sketchupScore += 1;
    else if (quizData.speedVsPower === 'learn-powerful') teklaScore += 1;
    
    if (quizData.speedVsAccuracy === 'speed-ease') sketchupScore += 1;
    else if (quizData.speedVsAccuracy === 'accuracy-depth') teklaScore += 1;
    
    if (quizData.softwarePreference === 'simple-visual') sketchupScore += 1;
    else if (quizData.softwarePreference === 'detailed-data') teklaScore += 1;
    
    if (quizData.siteChanges === 'quick-sketch') sketchupScore += 1;
    else if (quizData.siteChanges === 'update-model') teklaScore += 1;
    
    if (quizData.visualsVsPerformance === 'great-visuals') sketchupScore += 1;
    else if (quizData.visualsVsPerformance === 'high-performance') teklaScore += 1;
    
    // Practical Use & Business Goals Scoring
    if (quizData.pricingAccuracy === 'yes') teklaScore += 1;
    else if (quizData.pricingAccuracy === 'maybe-later') { sketchupScore += 0.5; teklaScore += 0.5; }
    else if (quizData.pricingAccuracy === 'not-now') sketchupScore += 1;
    
    if (quizData.quantitiesData === 'yes') teklaScore += 1;
    else if (quizData.quantitiesData === 'not-necessary') sketchupScore += 1;
    
    // Future Direction & Wrap-Up Scoring
    if (quizData.futureProjects === 'no') sketchupScore += 1;
    else if (quizData.futureProjects === 'possibly') { sketchupScore += 0.5; teklaScore += 0.5; }
    else if (quizData.futureProjects === 'definitely') teklaScore += 1;
    
    if (quizData.startSimple === 'start-simple') sketchupScore += 1;
    else if (quizData.startSimple === 'go-all-in') teklaScore += 1;
    
    if (quizData.motivation === 'improve-professionalism') sketchupScore += 1;
    else if (quizData.motivation === 'save-time') { sketchupScore += 0.5; teklaScore += 0.5; }
    else if (quizData.motivation === 'strengthen-engineering') teklaScore += 1;
    
    if (quizData.frustration === 'too-slow-hard') sketchupScore += 1;
    else if (quizData.frustration === 'not-detailed-enough') teklaScore += 1;
    else if (quizData.frustration === 'rely-others') sketchupScore += 1;
    
    // Determine recommendation
    let recommendation: string;
    let explanation: string;
    let benefits: string[];
    let nextSteps: string[];
    
    if (teklaScore > sketchupScore) {
      recommendation = "ScaffPlan for Tekla";
      explanation = "Based on what you've told us, ScaffPlan for Tekla is the best fit for you. You highlighted things like engineering integration, performance, and complex projects — all areas where Tekla shines. It's built for detailed, data-rich models and tube-and-fitting workflows. If you ever want faster visuals or quick client presentations, you can still use the SketchUp version alongside it. Many teams do both.";
      benefits = [
        "Engineering-level precision and integration",
        "High performance for large, complex models",
        "Detailed data and quantities for accurate pricing",
        "Professional compliance and sign-off capabilities"
      ];
      nextSteps = [
        "Start your Tekla free trial",
        "Schedule a technical demo",
        "Connect with our engineering team"
      ];
    } else if (sketchupScore > teklaScore) {
      recommendation = "ScaffPlan for SketchUp";
      explanation = "Based on what you've told us, ScaffPlan for SketchUp sounds like the best fit for you. You value speed, simplicity, and strong visuals — perfect for tenders, planning, and day-to-day operations. You'll be able to get results quickly without needing CAD experience. When you take on larger or more technical projects, you can always bring in the Tekla version for engineering-level depth.";
      benefits = [
        "Fast setup and easy learning curve",
        "Excellent visuals for client presentations",
        "Quick design iterations and changes",
        "Perfect for tenders and proposals"
      ];
      nextSteps = [
        "Start your SketchUp free trial",
        "Watch quick start tutorials",
        "Join our SketchUp community"
      ];
    } else {
      recommendation = "Both Could Work";
      explanation = "Based on what you've told us, both versions could fit your workflow. If you want to start fast and keep it simple, choose SketchUp. If you want full engineering capability and scalability, go Tekla. A lot of teams use both — SketchUp for visuals, Tekla for compliance.";
      benefits = [
        "Flexibility to choose based on project needs",
        "Can start with SketchUp and upgrade to Tekla",
        "Best of both worlds for different scenarios"
      ];
      nextSteps = [
        "Try both free trials",
        "Compare features side-by-side",
        "Speak with our team for guidance"
      ];
    }
    
    return {
      sketchupScore,
      teklaScore,
      recommendation,
      explanation,
      benefits,
      nextSteps
    };
  };

  const validateField = (fieldName: string, value: any): string => {
    switch (fieldName) {
      case 'cadExperience':
        return value === '' ? 'Please select your CAD experience level' : '';
      case 'confidence3D':
        return value === '' ? 'Please select your 3D design confidence' : '';
      case 'role':
        return value === '' ? 'Please select your role' : '';
      case 'projectTypes':
        return value === '' ? 'Please select your project types' : '';
      case 'scaffoldComplexity':
        return value === '' ? 'Please select scaffold complexity' : '';
      case 'materials':
        return value === '' ? 'Please select your materials' : '';
      case 'engineeringImportance':
        return value === '' ? 'Please select engineering importance' : '';
      case 'designCapacity':
        return value === '' ? 'Please select your design capacity' : '';
      case 'teamTechnical':
        return value === '' ? 'Please select team technical level' : '';
      case 'engineeringSignoff':
        return value === '' ? 'Please select engineering sign-off' : '';
      case 'priority':
        return value === '' ? 'Please select what\'s more important' : '';
      case 'speedVsPower':
        return value === '' ? 'Please select your preference' : '';
      case 'speedVsAccuracy':
        return value === '' ? 'Please select what matters more' : '';
      case 'softwarePreference':
        return value === '' ? 'Please select your software preference' : '';
      case 'siteChanges':
        return value === '' ? 'Please select how you respond to site changes' : '';
      case 'visualsVsPerformance':
        return value === '' ? 'Please select what\'s more important' : '';
      case 'tenderDesigns':
        return value === '' ? 'Please select about tender designs' : '';
      case 'pricingAccuracy':
        return value === '' ? 'Please select about pricing accuracy' : '';
      case 'quantitiesData':
        return value === '' ? 'Please select about quantities data' : '';
      case 'futureProjects':
        return value === '' ? 'Please select about future projects' : '';
      case 'startSimple':
        return value === '' ? 'Please select your approach' : '';
      case 'motivation':
        return value === '' ? 'Please select your motivation' : '';
      case 'frustration':
        return value === '' ? 'Please select your biggest frustration' : '';
      default:
        return '';
    }
  };

  const handleInputChange = (field: keyof QuizData, value: any) => {
    setQuizData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing/selecting
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Validate field on change
    const error = validateField(field, value);
    if (error) {
      setErrors(prev => ({ ...prev, [field]: error }));
    }
  };

  const nextStep = () => {
    if (!validateCurrentStep()) {
      // Ensure the user sees the first invalid input on small screens
      scrollToFirstErrorForStep(step);
      return;
    }
    
    if (step < 6) {
      setStep(step + 1);
    } else {
      setShowResults(true);
    }
  };

  const validateCurrentStep = (): boolean => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    switch (step) {
      case 1:
        // Experience & Background validation
        const cadExperienceError = validateField('cadExperience', quizData.cadExperience);
        if (cadExperienceError) {
          newErrors.cadExperience = cadExperienceError;
          isValid = false;
        }

        const confidence3DError = validateField('confidence3D', quizData.confidence3D);
        if (confidence3DError) {
          newErrors.confidence3D = confidence3DError;
          isValid = false;
        }

        const roleError = validateField('role', quizData.role);
        if (roleError) {
          newErrors.role = roleError;
          isValid = false;
        }
        break;
      
      case 2:
        // Projects & Work Type validation
        const projectTypesError = validateField('projectTypes', quizData.projectTypes);
        if (projectTypesError) {
          newErrors.projectTypes = projectTypesError;
          isValid = false;
        }

        const scaffoldComplexityError = validateField('scaffoldComplexity', quizData.scaffoldComplexity);
        if (scaffoldComplexityError) {
          newErrors.scaffoldComplexity = scaffoldComplexityError;
          isValid = false;
        }

        const materialsError = validateField('materials', quizData.materials);
        if (materialsError) {
          newErrors.materials = materialsError;
          isValid = false;
        }

        const engineeringImportanceError = validateField('engineeringImportance', quizData.engineeringImportance);
        if (engineeringImportanceError) {
          newErrors.engineeringImportance = engineeringImportanceError;
          isValid = false;
        }
        break;
      
      case 3:
        // Business & Resourcing validation
        const designCapacityError = validateField('designCapacity', quizData.designCapacity);
        if (designCapacityError) {
          newErrors.designCapacity = designCapacityError;
          isValid = false;
        }

        const teamTechnicalError = validateField('teamTechnical', quizData.teamTechnical);
        if (teamTechnicalError) {
          newErrors.teamTechnical = teamTechnicalError;
          isValid = false;
        }

        const engineeringSignoffError = validateField('engineeringSignoff', quizData.engineeringSignoff);
        if (engineeringSignoffError) {
          newErrors.engineeringSignoff = engineeringSignoffError;
          isValid = false;
        }
        break;

      case 4:
        // What Matters Most validation
        const priorityError = validateField('priority', quizData.priority);
        if (priorityError) {
          newErrors.priority = priorityError;
          isValid = false;
        }

        const speedVsPowerError = validateField('speedVsPower', quizData.speedVsPower);
        if (speedVsPowerError) {
          newErrors.speedVsPower = speedVsPowerError;
          isValid = false;
        }

        const speedVsAccuracyError = validateField('speedVsAccuracy', quizData.speedVsAccuracy);
        if (speedVsAccuracyError) {
          newErrors.speedVsAccuracy = speedVsAccuracyError;
          isValid = false;
        }

        const softwarePreferenceError = validateField('softwarePreference', quizData.softwarePreference);
        if (softwarePreferenceError) {
          newErrors.softwarePreference = softwarePreferenceError;
          isValid = false;
        }

        const siteChangesError = validateField('siteChanges', quizData.siteChanges);
        if (siteChangesError) {
          newErrors.siteChanges = siteChangesError;
          isValid = false;
        }

        const visualsVsPerformanceError = validateField('visualsVsPerformance', quizData.visualsVsPerformance);
        if (visualsVsPerformanceError) {
          newErrors.visualsVsPerformance = visualsVsPerformanceError;
          isValid = false;
        }
        break;

      case 5:
        // Practical Use & Business Goals validation
        const tenderDesignsError = validateField('tenderDesigns', quizData.tenderDesigns);
        if (tenderDesignsError) {
          newErrors.tenderDesigns = tenderDesignsError;
          isValid = false;
        }

        const pricingAccuracyError = validateField('pricingAccuracy', quizData.pricingAccuracy);
        if (pricingAccuracyError) {
          newErrors.pricingAccuracy = pricingAccuracyError;
          isValid = false;
        }

        const quantitiesDataError = validateField('quantitiesData', quizData.quantitiesData);
        if (quantitiesDataError) {
          newErrors.quantitiesData = quantitiesDataError;
          isValid = false;
        }
        break;

      case 6:
        // Future Direction & Wrap-Up validation
        const futureProjectsError = validateField('futureProjects', quizData.futureProjects);
        if (futureProjectsError) {
          newErrors.futureProjects = futureProjectsError;
          isValid = false;
        }

        const startSimpleError = validateField('startSimple', quizData.startSimple);
        if (startSimpleError) {
          newErrors.startSimple = startSimpleError;
          isValid = false;
        }

        const motivationError = validateField('motivation', quizData.motivation);
        if (motivationError) {
          newErrors.motivation = motivationError;
          isValid = false;
        }

        const frustrationError = validateField('frustration', quizData.frustration);
        if (frustrationError) {
          newErrors.frustration = frustrationError;
          isValid = false;
        }
        break;
    }

    setErrors(prev => ({ ...prev, ...newErrors }));
    return isValid;
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const resetQuiz = () => {
    setStep(1);
    setShowResults(false);
    setErrors({});
    setQuizData({
      cadExperience: '',
      confidence3D: '',
      role: '',
      projectTypes: '',
      scaffoldComplexity: '',
      materials: '',
      engineeringImportance: '',
      designCapacity: '',
      teamTechnical: '',
      engineeringSignoff: '',
      priority: '',
      speedVsPower: '',
      speedVsAccuracy: '',
      softwarePreference: '',
      siteChanges: '',
      visualsVsPerformance: '',
      tenderDesigns: '',
      pricingAccuracy: '',
      quantitiesData: '',
      futureProjects: '',
      startSimple: '',
      motivation: '',
      frustration: ''
    });
  };

  const renderErrorMessage = (fieldName: string) => {
    if (!errors[fieldName]) return null;
    return (
      <p className="mt-1 text-sm text-red-600 flex items-center">
        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        {errors[fieldName]}
      </p>
    );
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center pb-3 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 tracking-tight">Experience & Background</h2>
        <p className="text-gray-600 mt-1 font-medium text-sm">Tell us about your design experience and role</p>
      </div>
      
      <div className="w-full space-y-4">
        <div id="field-cadExperience">
          <label className={labelClass}>
            Do you have any experience using design or CAD software?
          </label>
          <CustomDropdown
            value={quizData.cadExperience}
            onChange={(value) => handleInputChange('cadExperience', value)}
            options={[
              { value: 'none', label: 'None at all' },
              { value: 'basic', label: 'A little with basic tools' },
              { value: 'advanced', label: 'Quite a lot with advanced tools' }
            ]}
            placeholder="Select your experience level"
            hasError={!!errors.cadExperience}
          />
          {renderErrorMessage('cadExperience')}
        </div>
        
        <div id="field-confidence3D">
          <label className={labelClass}>
            How confident are you at using 3D design tools?
          </label>
          <CustomDropdown
            value={quizData.confidence3D}
            onChange={(value) => handleInputChange('confidence3D', value)}
            options={[
              { value: 'not-confident', label: 'Not confident' },
              { value: 'fairly-confident', label: 'Fairly confident' },
              { value: 'very-confident', label: 'Very confident' }
            ]}
            placeholder="Select your confidence level"
            hasError={!!errors.confidence3D}
          />
          {renderErrorMessage('confidence3D')}
        </div>
        
        <div id="field-role">
          <label className={labelClass}>
            Which best describes your role?
          </label>
          <CustomDropdown
            value={quizData.role}
            onChange={(value) => handleInputChange('role', value)}
            options={[
              { value: 'designer-engineer', label: 'Scaffold Designer / Engineer' },
              { value: 'owner-manager-scaffolder', label: 'Owner / Project Manager / Scaffolder' }
            ]}
            placeholder="Select your role"
            hasError={!!errors.role}
          />
          {renderErrorMessage('role')}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center pb-3 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 tracking-tight">Projects & Work Type</h2>
        <p className="text-gray-600 mt-1 font-medium text-sm">Tell us about your typical projects and scaffold complexity</p>
      </div>
      
      <div className="w-full space-y-4">
        <div id="field-projectTypes">
          <label className={labelClass}>
            What types of projects do you generally work on?
          </label>
          <CustomDropdown
            value={quizData.projectTypes}
            onChange={(value) => handleInputChange('projectTypes', value)}
            options={[
              { value: 'small-residential', label: 'Small residential or low-rise' },
              { value: 'large-commercial', label: 'Large commercial' },
              { value: 'industrial-infrastructure', label: 'Industrial or infrastructure' }
            ]}
            placeholder="Select project types"
            hasError={!!errors.projectTypes}
          />
          {renderErrorMessage('projectTypes')}
        </div>
        
        <div id="field-scaffoldComplexity">
          <label className={labelClass}>
            How complex are your typical scaffolds?
          </label>
          <CustomDropdown
            value={quizData.scaffoldComplexity}
            onChange={(value) => handleInputChange('scaffoldComplexity', value)}
            options={[
              { value: 'simple-facades', label: 'Simple façades' },
              { value: 'mix-both', label: 'Mix of both' },
              { value: 'complex-multilevel', label: 'Complex and multi-level' }
            ]}
            placeholder="Select complexity level"
            hasError={!!errors.scaffoldComplexity}
          />
          {renderErrorMessage('scaffoldComplexity')}
        </div>
        
        <div id="field-materials">
          <label className={labelClass}>
            What materials or systems do you mainly use?
          </label>
          <CustomDropdown
            value={quizData.materials}
            onChange={(value) => handleInputChange('materials', value)}
            options={[
              { value: 'modular', label: 'Modular' },
              { value: 'tube-fitting', label: 'Tube and fitting' }
            ]}
            placeholder="Select materials"
            hasError={!!errors.materials}
          />
          {renderErrorMessage('materials')}
        </div>
        
        <div id="field-engineeringImportance">
          <label className={labelClass}>
            How important is it for your designs to link into engineering or compliance checks?
          </label>
          <CustomDropdown
            value={quizData.engineeringImportance}
            onChange={(value) => handleInputChange('engineeringImportance', value)}
            options={[
              { value: 'not-important', label: 'Not important' },
              { value: 'sometimes-helpful', label: 'Sometimes helpful' },
              { value: 'critical', label: 'Critical' }
            ]}
            placeholder="Select importance level"
            hasError={!!errors.engineeringImportance}
          />
          {renderErrorMessage('engineeringImportance')}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center pb-3 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 tracking-tight">Business & Resourcing</h2>
        <p className="text-gray-600 mt-1 font-medium text-sm">Tell us about your team and design capacity</p>
      </div>
      
      <div className="w-full space-y-4">
        <div id="field-designCapacity">
          <label className={labelClass}>
            Which best describes your current design capacity?
          </label>
          <CustomDropdown
            value={quizData.designCapacity}
            onChange={(value) => handleInputChange('designCapacity', value)}
            options={[
              { value: 'dedicated-team', label: 'Dedicated design team' },
              { value: 'one-designer', label: 'One designer' },
              { value: 'no-designer', label: 'No designer but want one' }
            ]}
            placeholder="Select design capacity"
            hasError={!!errors.designCapacity}
          />
          {renderErrorMessage('designCapacity')}
        </div>
        
        <div id="field-teamTechnical">
          <label className={labelClass}>
            How technical is your team overall?
          </label>
          <CustomDropdown
            value={quizData.teamTechnical}
            onChange={(value) => handleInputChange('teamTechnical', value)}
            options={[
              { value: 'not-very', label: 'Not very' },
              { value: 'mixed', label: 'Mixed' },
              { value: 'highly-technical', label: 'Highly technical' }
            ]}
            placeholder="Select technical level"
            hasError={!!errors.teamTechnical}
          />
          {renderErrorMessage('teamTechnical')}
        </div>
        
        <div id="field-engineeringSignoff">
          <label className={labelClass}>
            Who currently handles your engineering sign-offs?
          </label>
          <CustomDropdown
            value={quizData.engineeringSignoff}
            onChange={(value) => handleInputChange('engineeringSignoff', value)}
            options={[
              { value: 'external', label: 'External consultant' },
              { value: 'internal', label: 'Internal team' },
              { value: 'not-required', label: 'Not required' }
            ]}
            placeholder="Select sign-off method"
            hasError={!!errors.engineeringSignoff}
          />
          {renderErrorMessage('engineeringSignoff')}
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center pb-3 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 tracking-tight">What Matters Most</h2>
        <p className="text-gray-600 mt-1 font-medium text-sm">Help us understand your priorities and preferences</p>
      </div>
      
      <div className="w-full space-y-4">
        <div id="field-priority">
          <label className={labelClass}>
            What's more important to you right now?
          </label>
          <CustomDropdown
            value={quizData.priority}
            onChange={(value) => handleInputChange('priority', value)}
            options={[
              { value: 'costs-down', label: 'Keeping costs down' },
              { value: 'performance-capability', label: 'Performance and capability' }
            ]}
            placeholder="Select priority"
            hasError={!!errors.priority}
          />
          {renderErrorMessage('priority')}
        </div>

        <div id="field-speedVsPower">
          <label className={labelClass}>
            Would you rather...
          </label>
          <CustomDropdown
            value={quizData.speedVsPower}
            onChange={(value) => handleInputChange('speedVsPower', value)}
            options={[
              { value: 'get-running-fast', label: 'Get up and running fast' },
              { value: 'learn-powerful', label: 'Take time to learn something powerful' }
            ]}
            placeholder="Select preference"
            hasError={!!errors.speedVsPower}
          />
          {renderErrorMessage('speedVsPower')}
        </div>

        <div id="field-speedVsAccuracy">
          <label className={labelClass}>
            When designing, what matters more to you?
          </label>
          <CustomDropdown
            value={quizData.speedVsAccuracy}
            onChange={(value) => handleInputChange('speedVsAccuracy', value)}
            options={[
              { value: 'speed-ease', label: 'Speed and ease of use' },
              { value: 'accuracy-depth', label: 'Accuracy and depth' }
            ]}
            placeholder="Select what matters more"
            hasError={!!errors.speedVsAccuracy}
          />
          {renderErrorMessage('speedVsAccuracy')}
        </div>

        <div id="field-softwarePreference">
          <label className={labelClass}>
            Do you prefer software that feels...
          </label>
          <CustomDropdown
            value={quizData.softwarePreference}
            onChange={(value) => handleInputChange('softwarePreference', value)}
            options={[
              { value: 'simple-visual', label: 'Simple and visual' },
              { value: 'detailed-data', label: 'Detailed and data-driven' }
            ]}
            placeholder="Select preference"
            hasError={!!errors.softwarePreference}
          />
          {renderErrorMessage('softwarePreference')}
        </div>

        <div id="field-siteChanges">
          <label className={labelClass}>
            When site changes happen, how do you respond?
          </label>
          <CustomDropdown
            value={quizData.siteChanges}
            onChange={(value) => handleInputChange('siteChanges', value)}
            options={[
              { value: 'quick-sketch', label: 'Quickly sketch and keep moving' },
              { value: 'update-model', label: 'Update the model properly' }
            ]}
            placeholder="Select response"
            hasError={!!errors.siteChanges}
          />
          {renderErrorMessage('siteChanges')}
        </div>

        <div id="field-visualsVsPerformance">
          <label className={labelClass}>
            Which is more important to you?
          </label>
          <CustomDropdown
            value={quizData.visualsVsPerformance}
            onChange={(value) => handleInputChange('visualsVsPerformance', value)}
            options={[
              { value: 'great-visuals', label: 'Great visuals' },
              { value: 'high-performance', label: 'High performance for large models' }
            ]}
            placeholder="Select what's more important"
            hasError={!!errors.visualsVsPerformance}
          />
          {renderErrorMessage('visualsVsPerformance')}
        </div>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-6">
      <div className="text-center pb-3 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 tracking-tight">Practical Use & Business Goals</h2>
        <p className="text-gray-600 mt-1 font-medium text-sm">Tell us about your business objectives</p>
      </div>
      
      <div className="w-full space-y-4">
        <div id="field-tenderDesigns">
          <label className={labelClass}>
            Do you want to include scaffold designs in tenders or proposals?
          </label>
          <CustomDropdown
            value={quizData.tenderDesigns}
            onChange={(value) => handleInputChange('tenderDesigns', value)}
            options={[
              { value: 'yes', label: 'Yes' },
              { value: 'sometimes', label: 'Sometimes' },
              { value: 'not-really', label: 'Not really' }
            ]}
            placeholder="Select preference"
            hasError={!!errors.tenderDesigns}
          />
          {renderErrorMessage('tenderDesigns')}
        </div>

        <div id="field-pricingAccuracy">
          <label className={labelClass}>
            Would you like to use your scaffold designs to help quote or price jobs accurately?
          </label>
          <CustomDropdown
            value={quizData.pricingAccuracy}
            onChange={(value) => handleInputChange('pricingAccuracy', value)}
            options={[
              { value: 'yes', label: 'Yes' },
              { value: 'maybe-later', label: 'Maybe later' },
              { value: 'not-now', label: 'Not now' }
            ]}
            placeholder="Select preference"
            hasError={!!errors.pricingAccuracy}
          />
          {renderErrorMessage('pricingAccuracy')}
        </div>

        <div id="field-quantitiesData">
          <label className={labelClass}>
            Do you want your models to include quantities or load data?
          </label>
          <CustomDropdown
            value={quizData.quantitiesData}
            onChange={(value) => handleInputChange('quantitiesData', value)}
            options={[
              { value: 'yes', label: 'Yes' },
              { value: 'not-necessary', label: 'Not necessary' }
            ]}
            placeholder="Select preference"
            hasError={!!errors.quantitiesData}
          />
          {renderErrorMessage('quantitiesData')}
        </div>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-6">
      <div className="text-center pb-3 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 tracking-tight">Future Direction & Wrap-Up</h2>
        <p className="text-gray-600 mt-1 font-medium text-sm">Help us understand your future plans and motivations</p>
      </div>
      
      <div className="w-full space-y-4">
        <div id="field-futureProjects">
          <label className={labelClass}>
            Do you see your business taking on larger or more complex projects in the future?
          </label>
          <CustomDropdown
            value={quizData.futureProjects}
            onChange={(value) => handleInputChange('futureProjects', value)}
            options={[
              { value: 'no', label: 'No' },
              { value: 'possibly', label: 'Possibly' },
              { value: 'definitely', label: 'Definitely' }
            ]}
            placeholder="Select future plans"
            hasError={!!errors.futureProjects}
          />
          {renderErrorMessage('futureProjects')}
        </div>

        <div id="field-startSimple">
          <label className={labelClass}>
            Would you rather start simple and scale up later, or go all-in from the start?
          </label>
          <CustomDropdown
            value={quizData.startSimple}
            onChange={(value) => handleInputChange('startSimple', value)}
            options={[
              { value: 'start-simple', label: 'Start simple' },
              { value: 'go-all-in', label: 'Go all-in' }
            ]}
            placeholder="Select approach"
            hasError={!!errors.startSimple}
          />
          {renderErrorMessage('startSimple')}
        </div>

        <div id="field-motivation">
          <label className={labelClass}>
            What's driving you to explore design software right now?
          </label>
          <CustomDropdown
            value={quizData.motivation}
            onChange={(value) => handleInputChange('motivation', value)}
            options={[
              { value: 'improve-professionalism', label: 'Improve professionalism' },
              { value: 'save-time', label: 'Save time' },
              { value: 'strengthen-engineering', label: 'Strengthen engineering & pricing' }
            ]}
            placeholder="Select motivation"
            hasError={!!errors.motivation}
          />
          {renderErrorMessage('motivation')}
        </div>

        <div id="field-frustration">
          <label className={labelClass}>
            What's your biggest frustration with current scaffold designs?
          </label>
          <CustomDropdown
            value={quizData.frustration}
            onChange={(value) => handleInputChange('frustration', value)}
            options={[
              { value: 'too-slow-hard', label: 'Too slow or hard to learn' },
              { value: 'not-detailed-enough', label: 'Not detailed enough' },
              { value: 'rely-others', label: 'Rely on others' }
            ]}
            placeholder="Select frustration"
            hasError={!!errors.frustration}
          />
          {renderErrorMessage('frustration')}
        </div>
      </div>
    </div>
  );

  const renderResults = () => {
    const results = calculateRecommendations();
    
    return (
      <div className="h-full flex flex-col p-4">
        <div className="flex-grow overflow-y-auto space-y-6 px-6">
          <div className="text-center pb-3 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Your Product Fit Results</h2>
            <p className="text-gray-600 mt-1 font-medium text-sm">Based on your answers, here's which ScaffPlan version suits you best</p>
          </div>
          
          <div className={`rounded-2xl p-6 text-center ${
            results.recommendation === 'ScaffPlan for Tekla' ? 'bg-gradient-to-r from-blue-50 to-blue-100' :
            results.recommendation === 'ScaffPlan for SketchUp' ? 'bg-gradient-to-r from-green-50 to-green-100' :
            'bg-gradient-to-r from-yellow-50 to-yellow-100'
          }`}>
            <h3 className={`text-4xl font-bold mb-2 tracking-tight ${
              results.recommendation === 'ScaffPlan for Tekla' ? 'text-blue-600' :
              results.recommendation === 'ScaffPlan for SketchUp' ? 'text-green-600' :
              'text-yellow-600'
            }`}>
              {results.recommendation}
            </h3>
            <p className={`text-lg font-semibold tracking-tight ${
              results.recommendation === 'ScaffPlan for Tekla' ? 'text-blue-600' :
              results.recommendation === 'ScaffPlan for SketchUp' ? 'text-green-600' :
              'text-yellow-600'
            }`}>
              SketchUp Score: {results.sketchupScore} | Tekla Score: {results.teklaScore}
            </p>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h4 className="font-bold text-gray-900 mb-3 tracking-tight">Why This Recommendation?</h4>
            <p className="text-gray-700 leading-relaxed">{results.explanation}</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-bold text-gray-900 mb-3 tracking-tight">Key Benefits</h4>
              <div className="space-y-2 text-sm">
                {results.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center">
                    <svg className="w-4 h-4 text-blue-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h4 className="font-bold text-gray-900 mb-3 tracking-tight">Next Steps</h4>
              <div className="space-y-2 text-sm">
                {results.nextSteps.map((step, index) => (
                  <div key={index} className="flex items-center">
                    <div className="w-6 h-6 bg-[#0479d2] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3">
                      {index + 1}
                    </div>
                    <span className="text-gray-700">{step}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div className="pt-2 mt-2 border-t border-gray-200 flex-shrink-0">
          <div className="bg-[#d9ebf8] border border-[#0479d2] rounded-lg p-3 sm:p-6">
            <h4 className="font-bold text-[#0479d2] mb-2 sm:mb-3 text-center tracking-tight text-sm sm:text-base">Ready to transform your scaffold designs?</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
              <button
                onClick={() => setShowResults(false)}
                className="bg-white text-[#0479d2] border border-[#0479d2] px-4 sm:px-6 py-2 sm:py-3 rounded-full font-medium hover:bg-[#d9ebf8] transition-colors text-sm sm:text-base"
              >
                Edit Answers
              </button>
              <button
                onClick={() => window.open('https://www.scaffplan.com/scaffplan/trial', '_blank')}
                className="bg-[#045493] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full font-medium hover:bg-[#033a75] transition-colors text-sm sm:text-base"
              >
                Start Free Trial
              </button>
              <button
                onClick={() => window.open('https://www.scaffplan.com/scaffplan#demo', '_blank')}
                className="bg-white text-[#0479d2] border border-[#0479d2] px-4 sm:px-6 py-2 sm:py-3 rounded-full font-medium hover:bg-[#d9ebf8] transition-colors text-sm sm:text-base"
              >
                Watch a Demo
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Extract the main content into a separate function
  const renderContent = () => (
    <div className="w-full h-full flex flex-col">
      {/* Header */}
      <div className="bg-[#045493] text-white p-5 relative flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-medium tracking-tight">ScaffPlan Product Fit Quiz</h1>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={resetQuiz}
              className="text-white hover:text-blue-200 focus:outline-none flex items-center space-x-1"
              title="Start Over"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="text-sm">Restart</span>
            </button>
            {mode === 'modal' && onClose && (
              <button
                onClick={onClose}
                className="text-white hover:text-blue-200 focus:outline-none"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
        
        {!showResults && (
          <div className="mt-2 flex items-center space-x-2">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full ${
                  i <= step ? 'bg-white' : 'bg-[#0479d2]'
                }`}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Content */}
      <div className="flex-grow overflow-y-auto">
        {showResults ? (
          renderResults()
        ) : (
          <div className="bg-[#d9ebf8] p-4 h-full flex flex-col">
            <div className="flex-grow overflow-y-auto px-6">
              {/* Sticky error banner for small-height screens */}
              {(() => {
                const fields = stepFieldOrder[step] || [];
                const hasErrorsForStep = fields.some((f) => !!errors[f]);
                if (!hasErrorsForStep) return null;
                return (
                  <div className="sticky top-0 z-10 mb-4">
                    <div className="bg-red-50 border border-red-300 text-red-700 rounded-lg px-4 py-2 flex items-center justify-between">
                      <span className="font-medium">Please answer the highlighted question(s) below.</span>
                      <button
                        onClick={() => scrollToFirstErrorForStep(step)}
                        className="underline font-medium"
                      >
                        Jump to first
                      </button>
                    </div>
                  </div>
                );
              })()}
              {step === 1 && renderStep1()}
              {step === 2 && renderStep2()}
              {step === 3 && renderStep3()}
              {step === 4 && renderStep4()}
              {step === 5 && renderStep5()}
              {step === 6 && renderStep6()}
            </div>
            
            {/* Navigation */}
            <div className="relative flex justify-between items-center pt-4 border-t border-gray-200 mt-4">
              <div className="flex-1">
                {step > 1 && (
                  <button
                    onClick={prevStep}
                    className="px-8 py-3 rounded-full font-semibold text-base transition-colors bg-white text-[#0479d2] border border-[#0479d2] hover:bg-[#d9ebf8]"
                  >
                    Previous
                  </button>
                )}
              </div>
              
              <div className="absolute left-1/2 transform -translate-x-1/2">
                <span className="text-sm text-gray-500">
                  Step {step} of 6
                </span>
              </div>
              
              <div className="flex-1 flex justify-end">
                <button
                  onClick={nextStep}
                  className="bg-[#045493] text-white px-8 py-3 rounded-full font-semibold text-base hover:bg-[#033a75] transition-colors"
                >
                  {step === 6 ? 'Get Recommendations' : 'Next'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // For embedded mode, always show content
  if (mode === 'embedded') {
    return renderContent();
  }

  // For modal mode, check isOpen
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      {renderContent()}
    </div>
  );
}
